import React, { useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import ReportWidget from './components/ReportWidget';
import { fetchInventoryReport } from '../../store/reportingInventory/actions';

const PALETTE = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#0ea5e9', '#84cc16', '#f97316'];

const StockLevelsByCategoryWidget = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    inventoryReport: data,
    inventoryReportLoading: loading,
    inventoryReportError: error,
  } = useSelector((state) => state.ReportingInventory);

  useEffect(() => {
    if (!data && !loading && !error) dispatch(fetchInventoryReport());
  }, [dispatch, data, loading, error]);

  const handleRefresh = useCallback(() => {
    dispatch(fetchInventoryReport());
  }, [dispatch]);

  const chartData = useMemo(() => {
    const rows = data?.stockLevelsByPartCategory || [];
    return rows.map((row, index) => ({
      category: row.category,
      quantity: Number(row.totalQuantity) || 0,
      color: PALETTE[index % PALETTE.length],
    }));
  }, [data]);

  const isEmpty = chartData.length === 0;

  return (
    <ReportWidget
      title={t('business.inventory.stock_levels_by_category', 'Stock Levels by Part Category')}
      loading={loading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage={t('reporting.stock_levels_by_category.empty', 'No stock levels to display.')}
      onRefresh={handleRefresh}
    >
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={chartData}
            dataKey="quantity"
            nameKey="category"
            cx="50%"
            cy="50%"
            outerRadius={100}
            label={({ category, quantity }) => `${category}: ${quantity}`}
            labelLine={false}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip formatter={(value, name) => [value, name]} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </ReportWidget>
  );
};

export default StockLevelsByCategoryWidget;
